type Listener = () => void;

class Emitter {
  private listeners = new Set<Listener>();
  on(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }
  emit(): void {
    for (const l of this.listeners) l();
  }
}

const RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

export class AudioPlayer {
  private el: HTMLAudioElement;
  private rafHandle = 0;
  private events = {
    time: new Emitter(),
    state: new Emitter(),
    end: new Emitter(),
  };
  private rateIdx = 2;

  constructor() {
    this.el = document.createElement('audio');
    this.el.preload = 'metadata';
    (this.el as HTMLMediaElement & { playsInline?: boolean }).playsInline = true;
    this.el.addEventListener('play', () => {
      this.startRaf();
      this.events.state.emit();
    });
    this.el.addEventListener('pause', () => {
      this.stopRaf();
      this.events.state.emit();
    });
    this.el.addEventListener('loadedmetadata', () => this.events.state.emit());
    this.el.addEventListener('durationchange', () => this.events.state.emit());
    this.el.addEventListener('ended', () => {
      this.stopRaf();
      this.events.state.emit();
      this.events.end.emit();
    });
    this.el.addEventListener('seeked', () => this.events.time.emit());
  }

  get element(): HTMLAudioElement {
    return this.el;
  }
  get playing(): boolean {
    return !this.el.paused && !this.el.ended;
  }
  get currentTime(): number {
    return this.el.currentTime || 0;
  }
  get duration(): number {
    const d = this.el.duration;
    return Number.isFinite(d) ? d : 0;
  }
  get rate(): number {
    return RATES[this.rateIdx]!;
  }
  get muted(): boolean {
    return this.el.muted;
  }

  onTime(l: Listener): () => void {
    return this.events.time.on(l);
  }
  onState(l: Listener): () => void {
    return this.events.state.on(l);
  }
  onEnd(l: Listener): () => void {
    return this.events.end.on(l);
  }

  load(src: string): void {
    if (this.el.src === src || this.el.src.endsWith(src)) return;
    this.el.src = src;
    this.el.playbackRate = this.rate;
  }

  async play(): Promise<void> {
    try {
      await this.el.play();
    } catch (_) {
      /* autoplay blocked etc. */
    }
  }
  pause(): void {
    this.el.pause();
  }
  toggle(): void {
    if (this.playing) this.pause();
    else this.play();
  }
  seek(t: number): void {
    const dur = this.duration || 0;
    const clamped = Math.max(0, Math.min(dur > 0 ? dur - 0.05 : t, t));
    this.el.currentTime = clamped;
    this.events.time.emit();
  }
  skip(delta: number): void {
    this.seek(this.currentTime + delta);
  }
  cycleRate(dir: 1 | -1 = 1): number {
    this.rateIdx = (this.rateIdx + dir + RATES.length) % RATES.length;
    this.el.playbackRate = this.rate;
    this.events.state.emit();
    return this.rate;
  }
  setRateByIndex(i: number): void {
    if (i < 0 || i >= RATES.length) return;
    this.rateIdx = i;
    this.el.playbackRate = this.rate;
    this.events.state.emit();
  }
  static rates(): readonly number[] {
    return RATES;
  }
  toggleMute(): void {
    this.el.muted = !this.el.muted;
    this.events.state.emit();
  }

  private startRaf(): void {
    if (this.rafHandle) return;
    const tick = () => {
      this.events.time.emit();
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }
  private stopRaf(): void {
    if (this.rafHandle) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = 0;
    }
  }
}

export function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
